import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import "../styles/building-management.css";
import {
  getBuildingsPaginated,
  deleteBuilding,
  deleteBuildingsBulk,
} from "../services/buildingService";
import { useAppContext } from "../context/AppContext";
import ConfirmModal from "../components/ui/ConfirmModal";
import { toast } from "../utils/toast";
import { getActionDropdownDirection } from "../utils/dropdownPosition";

const BuildingManagementPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const lastLoadKeyRef = useRef("");
  const { setActiveSection, setBuildingId, setBuildingFormMode } =
    useAppContext();

  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [searchInput, setSearchInput] = useState(
    () => searchParams.get("search") || "",
  );
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(() => {
    const p = Number(searchParams.get("page") || 1);
    return Number.isInteger(p) && p > 0 ? p : 1;
  });
  const [limit, setLimit] = useState(() => {
    const l = Number(searchParams.get("limit") || 10);
    return Number.isInteger(l) && l > 0 ? l : 10;
  });
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1,
  });
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [dropdownDirection, setDropdownDirection] = useState("down");
  const [confirmDelete, setConfirmDelete] = useState({
    open: false,
    id: null,
    name: "",
    isBulk: false,
  });

  const reload = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getBuildingsPaginated({
        page,
        limit,
        search: debouncedSearch,
      });
      setBuildings(Array.isArray(result?.data) ? result.data : []);
      setPagination(
        result?.meta || {
          total: 0,
          page: 1,
          limit: Number(limit) || 10,
          totalPages: 1,
        },
      );
    } catch (e) {
      toast.error(e?.message || "Failed to load buildings");
    } finally {
      setLoading(false);
    }
  }, [page, limit, debouncedSearch]);

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch((searchInput || "").trim());
      setPage(1);
    }, 450);
    return () => clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    const pageStr = String(page);
    const limitStr = String(limit);
    const searchStr = String(searchInput || "").trim();

    if (params.get("page") !== pageStr) params.set("page", pageStr);
    if (params.get("limit") !== limitStr) params.set("limit", limitStr);
    if (searchStr) params.set("search", searchStr);
    else params.delete("search");

    const current = searchParams.toString();
    const next = params.toString();
    if (current !== next) {
      setSearchParams(params, { replace: true });
    }
  }, [page, limit, searchInput, searchParams, setSearchParams]);

  useEffect(() => {
    const key = `${page}:${limit}:${debouncedSearch}`;
    if (lastLoadKeyRef.current === key) return;
    lastLoadKeyRef.current = key;
    reload();
  }, [page, limit, debouncedSearch, reload]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeDropdown && !event.target.closest(".actions-menu-wrapper")) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [activeDropdown]);

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === "Escape") setActiveDropdown(null);
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, []);

  const filteredBuildings = useMemo(() => buildings || [], [buildings]);

  const allSelected = useMemo(
    () =>
      filteredBuildings.length > 0 &&
      selectedIds.size === filteredBuildings.length,
    [filteredBuildings, selectedIds],
  );

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredBuildings.map((b) => b.building_id)));
    }
  };

  const toggleSelect = (id) => {
    const next = new Set(selectedIds);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedIds(next);
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  const goToDetail = (id = null) => {
    if (id == null) return;
    setBuildingFormMode("view");
    setBuildingId(id);
    setActiveSection("building-detail", { buildingId: id });
  };

  const goToEdit = (id) => {
    if (id == null) return;
    setBuildingId(id);
    setActiveSection("building-edit", { buildingId: id });
  };

  const goToCreate = () => {
    setActiveSection("building-create");
  };

  const removeSingle = (id) => {
    const building = buildings.find((b) => b.building_id === id);
    const buildingName = building?.name || "Building";
    setConfirmDelete({
      open: true,
      id,
      name: buildingName,
      isBulk: false,
    });
  };

  const confirmRemoveSingle = async () => {
    const { id, name } = confirmDelete;
    setConfirmDelete({ open: false, id: null, name: "", isBulk: false });

    if (!id) {
      toast.error("No building selected for deletion");
      return;
    }

    try {
      await deleteBuilding(id);
      const label = name || "Building";
      toast.success(`${label} has been deleted successfully!`);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      await reload();
    } catch (e) {
      toast.error(e?.message || "Failed to delete building");
      await reload();
    }
  };

  const bulkDelete = () => {
    if (selectedIds.size === 0) return;
    const count = selectedIds.size;
    setConfirmDelete({
      open: true,
      id: null,
      name: "",
      isBulk: true,
      count,
    });
  };

  const confirmBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    const count = ids.length;
    setConfirmDelete({ open: false, id: null, name: "", isBulk: false });

    if (ids.length === 0) {
      toast.error("No buildings selected for deletion");
      return;
    }

    try {
      await deleteBuildingsBulk(ids);
      toast.success(
        `${count} building${count === 1 ? "" : "s"} deleted successfully!`,
      );
      setSelectedIds(new Set());
      await reload();
    } catch (e) {
      toast.error(e?.message || "Failed to delete selected buildings");
      await reload();
    }
  };

  const totalPages = Math.max(1, Number(pagination.totalPages) || 1);
  const currentPage = Math.min(
    Math.max(1, Number(pagination.page) || page),
    totalPages,
  );
  const totalRows = Number(pagination.total) || 0;
  const pageSize = Number(pagination.limit) || limit;
  const showingFrom = totalRows === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const showingTo =
    totalRows === 0 ? 0 : Math.min(currentPage * pageSize, totalRows);

  const visiblePageItems = useMemo(() => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    if (currentPage <= 2) {
      return [1, 2, 3, "ellipsis", totalPages];
    }

    if (currentPage === 3) {
      return [1, 2, 3, 4, "ellipsis", totalPages];
    }

    if (currentPage === totalPages - 2) {
      return [
        1,
        "ellipsis",
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    }

    if (currentPage >= totalPages - 1) {
      return [1, "ellipsis", totalPages - 2, totalPages - 1, totalPages];
    }

    return [
      1,
      "ellipsis",
      currentPage - 1,
      currentPage,
      currentPage + 1,
      "ellipsis",
      totalPages,
    ];
  }, [totalPages, currentPage]);

  const limitOptions = useMemo(() => {
    const base = [5, 10, 20, 50];
    const current = Number(limit);
    const all =
      Number.isInteger(current) && current > 0 ? [...base, current] : base;
    return Array.from(new Set(all)).sort((a, b) => a - b);
  }, [limit]);

  return (
    <div className="page-container building-page">
      <div className="page-header">
        <div className="header-content">
          <div>
            <h2 className="page-title">Building Management</h2>
            <p className="page-subtitle">
              View, create, edit, and delete buildings
            </p>
          </div>
          <div className="roles-toolbar">
            <div className="roles-search">
              <input
                className="search-input"
                type="text"
                placeholder="Search by name, company, code, country, city"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
              <button
                className="secondary-btn sm"
                onClick={() => setSearchInput("")}
                disabled={loading}
              >
                Clear
              </button>
            </div>
            <button
              className="primary-btn"
              onClick={goToCreate}
            >
              + Create Building
            </button>
          </div>
        </div>
      </div>

      <div className="page-content">
        <div className="building-pagination-top">
          <label htmlFor="building-limit" className="building-pagination-label">
            Rows per page:
          </label>
          <select
            id="building-limit"
            value={limit}
            onChange={(e) => {
              const next = Number(e.target.value) || 10;
              setLimit(next);
              setPage(1);
            }}
          >
            {limitOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>

        {selectedIds.size > 0 && (
          <div
            style={{
              marginBottom: "1rem",
              padding: "1rem",
              background: "var(--accent-tint-50)",
              borderRadius: "8px",
              border: "1px solid var(--accent-tint-100)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "0.75rem",
              }}
            >
              <span style={{ fontWeight: "600", color: "var(--accent-ink-800)" }}>
                {selectedIds.size}{" "}
                {selectedIds.size === 1 ? "building" : "buildings"} selected
              </span>
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                <button className="danger-btn sm" onClick={bulkDelete}>
                  Delete Selected
                </button>
                <button className="secondary-btn sm" onClick={clearSelection}>
                  Clear Selection
                </button>
              </div>
            </div>
          </div>
        )}

        {loading ?
          <div className="no-results">Loading buildings...</div>
        : <div className="roles-table">
            <div className="roles-table-header">
              <div className="cell checkbox">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleSelectAll}
                />
              </div>
              <div className="cell">Building Name</div>
              <div className="cell">Building Code</div>
              <div className="cell">Company</div>
              <div className="cell">Country</div>
              <div className="cell">City</div>
              <div className="cell actions">Actions</div>
            </div>

            {filteredBuildings.length === 0 ?
              <div className="no-results">No buildings found.</div>
            : filteredBuildings.map((b) => (
                <div
                  className={`roles-table-row ${
                    selectedIds.has(b.building_id) ? "selected" : ""
                  }`}
                  key={b.building_id}
                >
                  <div className="cell checkbox">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(b.building_id)}
                      onChange={() => toggleSelect(b.building_id)}
                    />
                  </div>
                  <div className="cell">{b.name}</div>

                  <div className="cell">{b.building_code || "-"}</div>
                  <div className="cell">{b.company?.name || "-"}</div>
                  <div className="cell">
                    {b.country?.countryName || b.country || "-"}
                  </div>
                  <div className="cell">
                    {b.city?.cityName || b.city || "-"}
                  </div>
                  <div className="cell actions">
                    <div
                      className="actions-menu-wrapper"
                      style={{ position: "relative", display: "inline-block" }}
                    >
                      <button
                        className="action-menu-btn"
                        aria-haspopup="true"
                        aria-expanded={activeDropdown === b.building_id}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (activeDropdown === b.building_id) {
                            setActiveDropdown(null);
                          } else {
                            setDropdownDirection(
                              getActionDropdownDirection(e.currentTarget),
                            );
                            setActiveDropdown(b.building_id);
                          }
                        }}
                      >
                        Actions ▼
                      </button>
                      {activeDropdown === b.building_id && (
                        <div
                          className={`action-dropdown-menu${
                            dropdownDirection === "up" ? " dropdown-up" : ""
                          }`}
                          role="menu"
                        >
                          <button
                            className="action-menu-item"
                            role="menuitem"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveDropdown(null);
                              goToDetail(b.building_id);
                            }}
                          >
                            View
                          </button>
                          <button
                            className="action-menu-item"
                            role="menuitem"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveDropdown(null);
                              goToEdit(b.building_id);
                            }}
                          >
                            Edit
                          </button>
                          <button
                            className="action-menu-item danger"
                            role="menuitem"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveDropdown(null);
                              removeSingle(b.building_id);
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            }
          </div>
        }

        <div className="building-pagination-bottom">
          <div className="building-pagination-summary">
            Showing {showingFrom} to {showingTo} of {totalRows} entries
          </div>
          <div className="building-pagination-actions">
            <button
              className="secondary-btn sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={currentPage <= 1 || loading}
            >
              Previous
            </button>
            {visiblePageItems.map((item, index) => {
              if (item === "ellipsis") {
                return (
                  <span
                    key={`ellipsis-${index}`}
                    className="secondary-btn sm"
                    aria-hidden="true"
                  >
                    ...
                  </span>
                );
              }
              const p = Number(item);
              return (
                <button
                  key={p}
                  className={
                    p === currentPage ? "primary-btn sm" : "secondary-btn sm"
                  }
                  onClick={() => setPage(p)}
                  disabled={loading}
                >
                  {p}
                </button>
              );
            })}
            <button
              className="secondary-btn sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages || loading}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <ConfirmModal
        open={confirmDelete.open}
        title="Confirm Delete"
        message={
          confirmDelete.isBulk ?
            `Are you sure you want to delete ${confirmDelete.count} building(s)? This action cannot be undone.`
          : `Are you sure you want to delete building "${confirmDelete.name}"? This action cannot be undone.`
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={
          confirmDelete.isBulk ? confirmBulkDelete : confirmRemoveSingle
        }
        onCancel={() =>
          setConfirmDelete({ open: false, id: null, name: "", isBulk: false })
        }
        isDanger={true}
      />
    </div>
  );
};

export default BuildingManagementPage;

